import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  archiveClub,
  archiveCommunity,
  archiveOrganization,
  unarchiveClub,
  unarchiveCommunity,
  unarchiveOrganization,
} from '../../../services/superAdminService';
import { fetchSaClubs, fetchSaCommunities, fetchSaOrganizations } from '../api/saApi';
import { createClub, fetchClubAdmins } from '../../../services/clubService';
import { createCommunity, fetchCommunityAdmins } from '../../../services/communityService';
import { createOrganization, fetchOrganizationAdmins } from '../../../services/organizationService';
import ConfirmationDialog from './ConfirmationDialog';
import DetailDrawer from './DetailDrawer';
import FeedbackState from './FeedbackState';
import FilterBar from './FilterBar';
import ResourceDataTable from './ResourceDataTable';
import StatusChip from './StatusChip';
import { formatAdminDate, presentEntityStatus, safeAdminError, shortenIdentifier } from './adminPresentation';

const CONFIG = {
  club: {
    singular: 'club',
    plural: 'clubs',
    responseKey: 'clubs',
    fetchEntities: fetchSaClubs,
    fetchAdmins: fetchClubAdmins,
    createEntity: createClub,
    archiveEntity: archiveClub,
    unarchiveEntity: unarchiveClub,
    detailsRoute: (id) => `/sa/clubs/${id}`,
  },
  organization: {
    singular: 'organization',
    plural: 'organizations',
    responseKey: 'organizations',
    fetchEntities: fetchSaOrganizations,
    fetchAdmins: fetchOrganizationAdmins,
    createEntity: createOrganization,
    archiveEntity: archiveOrganization,
    unarchiveEntity: unarchiveOrganization,
    detailsRoute: (id) => `/sa/organizations/${id}`,
  },
  community: {
    singular: 'community',
    plural: 'communities',
    responseKey: 'communities',
    fetchEntities: fetchSaCommunities,
    fetchAdmins: fetchCommunityAdmins,
    createEntity: createCommunity,
    archiveEntity: archiveCommunity,
    unarchiveEntity: unarchiveCommunity,
    detailsRoute: (id) => `/sa/communities/${id}`,
  },
};

const EntityDirectoryCards = ({ entityType, archived = false }) => {
  const config = CONFIG[entityType];
  const { token } = useAuth();
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [newEntityName, setNewEntityName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [creationStatus, setCreationStatus] = useState({ message: '', tone: 'info' });
  const [admins, setAdmins] = useState([]);
  const [isLoadingAdminCounts, setIsLoadingAdminCounts] = useState(true);
  const [adminCountsError, setAdminCountsError] = useState('');
  const [confirmModal, setConfirmModal] = useState({ id: '', action: '' });
  const [isSavingArchive, setIsSavingArchive] = useState(false);
  const [inspection, setInspection] = useState(null);

  const loadEntities = useCallback(async () => {
    if (!token) {
      setEntities([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await config.fetchEntities({ token, view: archived ? 'archived' : 'active' });
      setEntities(response[config.responseKey] || []);
    } catch (err) {
      setError(safeAdminError(err, `Unable to load ${config.plural}.`));
    } finally {
      setIsLoading(false);
    }
  }, [archived, config, token]);

  const loadAdmins = useCallback(async () => {
    if (!token) {
      setAdmins([]);
      setIsLoadingAdminCounts(false);
      return;
    }

    setIsLoadingAdminCounts(true);

    try {
      const adminResponse = await config.fetchAdmins(token);
      setAdmins(adminResponse.admins || []);
      setAdminCountsError('');
    } catch (err) {
      setAdminCountsError(safeAdminError(err, `Unable to load ${config.singular} administrators.`));
    } finally {
      setIsLoadingAdminCounts(false);
    }
  }, [config, token]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  useEffect(() => {
    const handleRoleAssigned = (event) => {
      const assignedEntityType = event?.detail?.entityType;
      if (assignedEntityType === config.singular) {
        loadAdmins();
        loadEntities();
      }
    };

    window.addEventListener('entity-admin-role-assigned', handleRoleAssigned);
    return () => window.removeEventListener('entity-admin-role-assigned', handleRoleAssigned);
  }, [config.singular, loadAdmins, loadEntities]);

  const handleCreate = async (event) => {
    event.preventDefault();

    if (!newEntityName.trim()) {
      setCreationStatus({ message: `Please enter a ${config.singular} name.`, tone: 'danger' });
      return;
    }

    setIsCreating(true);
    setCreationStatus({ message: '', tone: 'info' });

    try {
      await config.createEntity({ name: newEntityName.trim() }, token);
      await loadEntities();
      setCreationStatus({ message: `${config.singular[0].toUpperCase()}${config.singular.slice(1)} created successfully.`, tone: 'success' });
      setNewEntityName('');
    } catch (err) {
      setCreationStatus({ message: safeAdminError(err, `Unable to create ${config.singular}.`), tone: 'danger' });
    } finally {
      setIsCreating(false);
    }
  };

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return entities.filter((entity) => entity.name?.toLowerCase().includes(term));
  }, [entities, searchTerm]);

  // UI source of truth for card counts is active admin memberships fetched via /admins
  // and grouped per entity; API adminsCount is used as fallback.
  const adminCounts = useMemo(
    () =>
      admins.reduce((counts, admin) => {
        const entity = admin[config.singular];
        const entityId = entity?._id || entity?.id;
        if (entityId) counts[entityId] = (counts[entityId] || 0) + 1;
        return counts;
      }, {}),
    [admins, config.singular],
  );

  const performArchiveAction = async () => {
    const { id, action } = confirmModal;
    if (!id || !action) return;

    setIsSavingArchive(true);

    try {
      const response = action === 'archive'
        ? await config.archiveEntity(id, token)
        : await config.unarchiveEntity(id, token);
      setCreationStatus({ message: response.message || `${config.singular} updated successfully.`, tone: 'success' });
      await loadEntities();
      setConfirmModal({ id: '', action: '' });
    } catch (err) {
      setCreationStatus({ message: safeAdminError(err, `Unable to update ${config.singular}.`), tone: 'danger' });
    } finally {
      setIsSavingArchive(false);
    }
  };

  const selectedEntity = entities.find((item) => (item._id || item.id) === confirmModal.id);

  const entityLabel = (entity) => entity.name || `${config.singular[0].toUpperCase()}${config.singular.slice(1)} ${shortenIdentifier(entity._id || entity.id)}`;
  const columns = [
    { key: 'name', label: 'Name', sortable: true, render: entityLabel },
    { key: 'type', label: 'Type', render: () => `${config.singular[0].toUpperCase()}${config.singular.slice(1)}` },
    { key: 'admins', label: 'Administrators', numeric: true, render: (entity) => adminCounts[entity._id || entity.id] ?? entity.adminsCount ?? 0 },
    { key: 'status', label: 'Status', render: (entity) => <StatusChip value={presentEntityStatus(entity).label} /> },
    { key: 'createdAt', label: 'Created', sortable: true, sortValue: (entity) => entity.createdAt, render: (entity) => formatAdminDate(entity.createdAt) },
  ];

  return (
    <div className="sa-resource-index">
      <FilterBar searchPlaceholder={`Search ${config.plural}`} searchValue={searchTerm} onSearchChange={setSearchTerm} hasActiveFilters={Boolean(searchTerm)} onClear={() => setSearchTerm('')} />

      {!archived && (
        <form className="row g-3 align-items-end" onSubmit={handleCreate}>
          <div className="col-lg-6">
            <label className="form-label" htmlFor="newEntityName">{config.singular[0].toUpperCase()}{config.singular.slice(1)} name</label>
            <input
              id="newEntityName"
              name="newEntityName"
              type="text"
              className="form-control"
              placeholder={`Enter ${config.singular} name`}
              value={newEntityName}
              onChange={(event) => setNewEntityName(event.target.value)}
            />
          </div>
          <div className="col-lg-3 d-grid">
            <button type="submit" className="btn btn-primary" disabled={!token || isCreating}>
              {isCreating ? 'Creating...' : `Create ${config.singular}`}
            </button>
          </div>
        </form>
      )}

      {creationStatus.message && (
        <div className={`sa-inline-feedback sa-inline-feedback--${creationStatus.tone === 'success' ? 'success' : 'danger'}`} role="status">
          {creationStatus.message}
        </div>
      )}
      {adminCountsError ? <FeedbackState type="warning" title="Administrator counts unavailable" message={adminCountsError} /> : null}
      <ResourceDataTable caption={`${config.plural} directory`} items={filtered} columns={columns} loading={isLoading || isLoadingAdminCounts} error={error} onRetry={loadEntities} getRowLabel={entityLabel} onInspect={setInspection} emptyTitle={searchTerm ? 'No matching records' : `No ${archived ? 'archived ' : ''}${config.plural}`} emptyMessage={searchTerm ? 'Clear the search or try a different name.' : `${config.plural[0].toUpperCase()}${config.plural.slice(1)} will appear here when available.`} getActions={(entity) => [{ label: 'View full details', onSelect: () => navigate(config.detailsRoute(entity._id || entity.id), { state: { [config.singular]: entity } }) }, { label: 'Copy ID', onSelect: () => navigator.clipboard?.writeText(entity._id || entity.id) }, { label: archived ? 'Restore' : 'Archive', danger: !archived, onSelect: () => setConfirmModal({ id: entity._id || entity.id, action: archived ? 'unarchive' : 'archive' }) }]} />
      <DetailDrawer open={Boolean(inspection)} title={inspection ? entityLabel(inspection) : ''} eyebrow={`${config.singular} overview`} onClose={() => setInspection(null)} developerData={inspection}><section><h3>Overview</h3><dl className="sa-detail-list"><div><dt>Status</dt><dd><StatusChip value={inspection ? presentEntityStatus(inspection).label : 'Inactive'} /></dd></div><div><dt>Administrators</dt><dd>{inspection ? adminCounts[inspection._id || inspection.id] ?? inspection.adminsCount ?? 0 : 0}</dd></div><div><dt>Created</dt><dd>{formatAdminDate(inspection?.createdAt)}</dd></div><div><dt>Identifier</dt><dd>{shortenIdentifier(inspection?._id || inspection?.id)}</dd></div></dl></section><div className="sa-drawer__actions"><button className="sa-primary-button" type="button" onClick={() => navigate(config.detailsRoute(inspection._id || inspection.id), { state: { [config.singular]: inspection } })}>View full details</button></div></DetailDrawer>
      <ConfirmationDialog open={Boolean(confirmModal.id)} title={`${archived ? 'Restore' : 'Archive'} this ${config.singular}?`} entityName={selectedEntity?.name} consequence={archived ? `This ${config.singular} will return to the active directory.` : `This ${config.singular} will be removed from active lists. Existing records are retained.`} unaffected="Historical records and related cricket data are not deleted." confirmLabel={archived ? 'Restore' : 'Archive'} tone={archived ? 'primary' : 'danger'} busy={isSavingArchive} onCancel={() => setConfirmModal({ id: '', action: '' })} onConfirm={performArchiveAction} />
    </div>
  );
};

export default EntityDirectoryCards;
